import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useAtomValue } from 'jotai';
import dayjs from 'dayjs';
import { SccPressable } from '@/components/SccPressable';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import React from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';
import BookmarkIcon from '@/assets/icon/ic_bookmark.svg';
import ShareIcon from '@/assets/icon/ic_share.svg';
import { currentLocationAtom } from '@/atoms/Location';
import Tags from '@/components/Tag';
import { accessibilitySourceLabel, } from '@/components/toilet/data';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import useNavigation from '@/navigation/useNavigation';
import AvailableLabel from '@/screens/ToiletDetailScreen/AvailableLabel';
import ImageList from '@/screens/PlaceDetailScreen/components/PlaceDetailImageList';
import { distanceInMeter, prettyFormatMeter } from '@/utils/DistanceUtils';
import ToastUtils from '@/utils/ToastUtils.ts';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
export default function ToiletCard({ item }) {
    var _a, _b;
    const navigation = useNavigation();
    const currentLocation = useAtomValue(currentLocationAtom);
    const distanceText = (() => {
        let distance;
        if (currentLocation && item.location) {
            distance = distanceInMeter(currentLocation, {
                latitude: item.location.lat,
                longitude: item.location.lng,
            });
        }
        else {
            distance = undefined;
        }
        return prettyFormatMeter(distance);
    })();
    const images = item.imageUrl ? [{ imageUrl: item.imageUrl }] : [];
    const hasImage = images.length > 0;
    const onShare = () => {
        ToastUtils.show('준비 중입니다.');
    };
    const onBookmark = () => {
        ToastUtils.show('준비 중입니다.');
    };
    const tagTexts = [(_a = item.gender) === null || _a === void 0 ? void 0 : _a.desc, (_b = item.entrance) === null || _b === void 0 ? void 0 : _b.desc].filter(Boolean);
    return (_jsx(LogParamsProvider, { params: {
            toilet_id: item.toiletId,
        }, children: _jsxs(Container, { elementName: "toilet_card", hasImage: hasImage, onPress: () => {
                navigation.navigate('ToiletDetail', {
                    toiletId: item.toiletId,
                });
            }, children: [_jsxs(InfoArea, { children: [_jsxs(LabelIconArea, { children: [item.available ? (_jsx(AvailableLabel, { availableState: item.available.state, text: item.available.desc })) : (_jsx(View, {})), _jsxs(IconArea, { children: [_jsx(SccTouchableOpacity, { elementName: "toilet_card_share_button", activeOpacity: 0.6, onPress: onShare, children: _jsx(ShareIcon, { color: color.gray80 }) }), _jsx(SccTouchableOpacity, { elementName: "toilet_card_bookmark_button", activeOpacity: 0.6, onPress: onBookmark, children: _jsx(BookmarkIcon, { color: color.gray80 }) })] })] }), _jsxs(TitleArea, { children: [_jsx(TitleText, { children: item.name }), _jsxs(LocationBox, { children: [_jsx(DistanceText, { children: distanceText }), _jsx(LocationDivider, {}), _jsx(AddressText, { numberOfLines: 1, ellipsizeMode: "tail", children: item.address })] })] }), tagTexts.length > 0 && (_jsx(ExtraArea, { children: _jsx(Tags, { texts: tagTexts }) })), item.source != null && (_jsxs(SourceMetaRow, { children: [_jsx(SourceNameText, { children: accessibilitySourceLabel(item.source) }), item.lastVerifiedAt != null && (_jsxs(SourceDateText, { children: [dayjs(item.lastVerifiedAt.value).format('YYYY.MM.DD'), " \uD655\uC778"] }))] }))] }), hasImage && (_jsx(View, { style: { width: '100%', flexShrink: 2, overflow: 'hidden' }, children: _jsx(ImageList, { images: images }) }))] }) }));
}
const InfoArea = styled.View `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;
const LabelIconArea = styled.View `
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;
const TitleArea = styled.View `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 8px;
  width: 100%;
`;
const ExtraArea = styled.View `
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
`;
const IconArea = styled.View `
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;
const Container = styled(SccPressable) `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  ${({ hasImage }) => (hasImage ? 'height: 230px;' : '')}
`;
const TitleText = styled.Text `
  font-size: 16px;
  font-family: ${() => font.pretendardBold};
  color: ${() => color.black};
`;
const DistanceText = styled.Text `
  font-size: 14px;
  font-family: ${() => font.pretendardMedium};
  color: ${() => color.gray80};
`;
const AddressText = styled.Text `
  flex: 1;
  font-size: 14px;
  font-family: ${() => font.pretendardRegular};
  color: ${() => color.gray80};
`;
const LocationBox = styled.View `
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  width: 100%;
`;
const LocationDivider = styled.View `
  width: 2px;
  height: 2px;
  border-radius: 1px;
  background-color: ${() => color.gray80};
`;
const SourceMetaRow = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  flex-wrap: wrap;
`;
const SourceNameText = styled.Text `
  font-size: 11px;
  font-family: ${() => font.pretendardRegular};
  color: ${() => color.gray60};
`;
const SourceDateText = styled.Text `
  font-size: 11px;
  font-family: ${() => font.pretendardRegular};
  color: ${() => color.gray50};
`;
